import React, { useMemo } from 'react';
import parse, { domToReact } from 'html-react-parser';
import cx from 'classnames';
import { isTag, isText } from 'domhandler';
import ParsedTextProps from './ParsedText.types';
import styles from './ParsedText.module.scss';
import Text from '../Text';
import { Sizing } from '../Text/Text.types';
import Link from '../Link';
import ToolTip from '../../molecules/ToolTip';
import PhoneLink from '../PhoneLink';
import { AppearsFromType } from '../../molecules/ToolTip/ToolTip.types';
import { StyleSheetColorType } from '../../shared/types/styleSheetColors.types';
import { TextColor, TextColorType } from '../../shared/types/textColor.types';
import Modal from '../../molecules/Modal';
import Button from '../Button';
import Heading from '../Heading';
import { HeadingTagsType } from '../Heading/Heading.types';
import Container from '../../templates/Container';

const ParsedText = ({
  htmlString,
  textProps,
  dataCy,
  sizing = Sizing.Regular,
  color = TextColor.Primary,
}: ParsedTextProps) => {
  const parsedContent = useMemo(() => {
    const options = {
      replace: (domNode: any) => {
        if (!isTag(domNode)) return null;
        switch (domNode.name) {
          // anchor links
          case 'a': {
            const { href, target } = domNode.attribs;
            const child = domNode.children[0];
            return (
              <Link key={href} href={href} target={target}>
                {isText(child) && child.data}
              </Link>
            );
          }
          // strong bold text
          case 'strong': {
            const { href } = domNode.attribs;
            const child = domNode.children[0];
            return (
              <strong key={href} className={cx(styles.parsedText, styles[color])}>
                {isText(child) && child.data}
              </strong>
            );
          }
          // breakpoint
          case 'br': {
            return <br />;
          }
          // para / paragraph;
          case 'p': {
            return <p>{domToReact(domNode.children, options)}</p>;
          }
          // images
          case 'img': {
            const { src, alt, width, height } = domNode.attribs;
            return <img src={src} alt={alt} width={width} height={height} />;
          }
          // tooltips
          case 'tooltip': {
            const { appearsfrom, fillcolor, text, color } = domNode.attribs;
            const child = domNode.children[0];

            return (
              <ToolTip
                appearsFrom={appearsfrom as AppearsFromType}
                fillColor={fillcolor as StyleSheetColorType}
                color={color as TextColorType}
                text={text}
                className={styles.tooltip}>
                {isText(child) && child.data}
              </ToolTip>
            );
          }
          // phonelink
          case 'phonelink': {
            const { dataCy } = domNode.attribs;
            const child = domNode.children[0];
            return <PhoneLink dataCy={dataCy}>{isText(child) && child.data}</PhoneLink>;
          }
          // modal
          case 'modal': {
            const { closedOnload, pageContentSelector, trigger, clickOffToClose } = domNode.attribs;

            return (
              <Modal
                closedOnload={closedOnload === 'true'}
                pageContentSelector={pageContentSelector}
                trigger={<Button variant='link'>{trigger}</Button>}
                clickOffToClose={clickOffToClose === 'true'}>
                {domToReact(domNode.children, options)}
              </Modal>
            );
          }
          // container
          case 'container': {
            const { maxWidth, padding } = domNode.attribs;

            return (
              <Container maxWidth={maxWidth} padding={padding}>
                {domToReact(domNode.children, options)}
              </Container>
            );
          }
          // heading
          case 'heading': {
            const { heading } = domNode.attribs;
            const child = domNode.children[0];
            return <Heading heading={heading as HeadingTagsType}>{isText(child) && child.data}</Heading>;
          }
          // text
          case 'text': {
            const child = domNode.children[0];
            return <Text {...textProps}>{isText(child) && child.data}</Text>;
          }
          // button
          case 'button': {
            const { dataModalclose } = domNode.attribs;
            const child = domNode.children[0];
            return <Button data-modalclose={dataModalclose === 'true'}>{isText(child) && child.data}</Button>;
          }
          // removes any other tag
          default:
            return <>{domToReact(domNode.children, options)}</>;
        }
      },
    };

    return parse(htmlString, options);
  }, [htmlString]);

  return (
    <Text dataCy={dataCy} sizing={sizing} className={cx(styles.parsedText, textProps?.className)} {...textProps}>
      {parsedContent}
    </Text>
  );
};

export default ParsedText;
